/**
 * Sales Overview Page
 *
 * Main dashboard showing aggregated sales metrics, category breakdown,
 * and top projects for admin users.
 */

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSalesOverview, useTopProjects, useCurrentPeriod, useMonthlySalesTrend, useTopCustomersGlobal } from "../hooks/useSalesData";
import { PeriodSelector } from "../components/sales/PeriodSelector";
import { CategoryChart } from "../components/sales/CategoryChart";
import { CategoryGrowthChart } from "../components/sales/CategoryGrowthChart";
import { TrendChart } from "../components/sales/TrendChart";
import { MonthlySalesTrendChart } from "../components/sales/MonthlySalesTrendChart";
import { TopProjectsTable } from "../components/sales/TopProjectsTable";
import { TopCustomersCard } from "../components/sales/TopCustomersCard";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { DollarSign, FileText, Users, TrendingUp } from "lucide-react";
import { t } from "@/shared/lib/th-translations";

export default function SalesOverview() {
  const [searchParams] = useSearchParams();
  const currentPeriod = useCurrentPeriod();
  const [selectedPeriod, setSelectedPeriod] = useState(currentPeriod);
  const [includeComparison] = useState(true);
  const [trendMetric, setTrendMetric] = useState<'revenue' | 'sales'>('revenue');

  // Get preview mode from URL (admin feature)
  const previewAs = searchParams.get("previewAs") || undefined;

  // Fetch data with React Query
  const { data: overview, isLoading: overviewLoading } = useSalesOverview({
    period: selectedPeriod,
    includeComparison,
    previewAs
  });

  const { data: topProjects, isLoading: projectsLoading } = useTopProjects({
    period: selectedPeriod,
    limit: 10,
    previewAs
  });

  // Fetch monthly trend data for current year with selected metric
  const currentYear = new Date().getFullYear();
  const { data: monthlyTrend } = useMonthlySalesTrend(currentYear, trendMetric, previewAs);

  // Fetch top customers for current period
  const { data: topCustomersData } = useTopCustomersGlobal({
    period: selectedPeriod,
    limit: 10,
    previewAs
  });

  const isLoading = overviewLoading || projectsLoading;

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t("salesOverview.title")}</h1>
          <p className="text-muted-foreground">
            {t("salesOverview.subtitle")}
          </p>
        </div>
        <PeriodSelector value={selectedPeriod} onChange={setSelectedPeriod} />
      </div>

      {/* Summary Cards */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t("salesOverview.stats.totalRevenue")}</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">฿{overview.totalRevenue.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">
                {overview.period.year}-{String(overview.period.month).padStart(2, '0')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t("salesOverview.stats.totalProjects")}</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{overview.totalProjects}</div>
              <p className="text-xs text-muted-foreground">{t("salesOverview.stats.completedInvoices")}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t("salesOverview.stats.activeSalespersons")}</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{overview.salespersonsCount}</div>
              <p className="text-xs text-muted-foreground">{t("salesOverview.stats.withSalesThisPeriod")}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t("salesOverview.stats.avgProjectValue")}</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ฿{overview.totalProjects > 0
                  ? Math.round(overview.totalRevenue / overview.totalProjects).toLocaleString()
                  : 0}
              </div>
              <p className="text-xs text-muted-foreground">{t("salesOverview.stats.perProject")}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Monthly Trend Chart - Full Width */}
      {monthlyTrend && (
        <MonthlySalesTrendChart
          data={monthlyTrend.months}
          metric={trendMetric}
          onMetricChange={setTrendMetric}
          title={t("salesOverview.charts.salesTrend")}
          description={t("salesOverview.charts.salesTrendDesc")}
        />
      )}

      {/* Top Customers and Category Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {topCustomersData && (
          <TopCustomersCard
            customers={topCustomersData.customers}
            totalCustomers={topCustomersData.totalCustomers}
            period={selectedPeriod}
            title={t("salesOverview.charts.topCustomers")}
            description={t("salesOverview.charts.topCustomersDesc")}
          />
        )}
        {overview && (
          <CategoryChart
            data={overview.categoryBreakdown}
            title={t("salesOverview.charts.revenueByCategory")}
            description={t("salesOverview.charts.revenueByCategoryDesc")}
          />
        )}
      </div>

      {/* Period Comparison */}
      {overview?.periodComparison && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TrendChart
            comparison={overview.periodComparison}
            title={t("salesOverview.charts.periodComparison")}
            description={t("salesOverview.charts.periodComparisonDesc")}
          />
        </div>
      )}

      {/* Category Growth Visualization Charts */}
      {overview?.periodComparison && overview.categoryBreakdown.some(cat => cat.growth) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CategoryGrowthChart
            data={overview.categoryBreakdown}
            chartType="bar"
            metricType="revenue"
            title={t("salesOverview.charts.revenueGrowth")}
            description={t("salesOverview.charts.revenueGrowthDesc")}
          />
          <CategoryGrowthChart
            data={overview.categoryBreakdown}
            chartType="bar"
            metricType="projectCount"
            title={t("salesOverview.charts.projectCountGrowth")}
            description={t("salesOverview.charts.projectCountGrowthDesc")}
          />
        </div>
      )}

      {/* Top Projects */}
      {topProjects && (
        <TopProjectsTable
          projects={topProjects.projects}
          title={t("salesOverview.charts.topProjects")}
          description={t("salesOverview.charts.topProjectsDesc")}
        />
      )}
    </div>
  );
}
